import { Box } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';

import {
    getRouteForbidden,
    getRouteReportsSpendSummary,
    getRouteReportsSpendSummaryCategory,
    getRouteReportsSpendSummaryProduct,
    getRouteReportsSpendSummarySupplier,
} from 'shared/const/router';
import { clampToZero } from 'shared/lib/number/number';
import { formatReportCurrency } from 'shared/lib/reportFormatters';
import { ReportDrillInHeader } from 'shared/ui/ReportDrillInHeader';
import { ReportMetric } from 'shared/ui/ReportMetric';
import { Page } from 'widgets/Page';
import { SavingsSummaryProductDetailsDrawer } from 'widgets/SavingsSummaryReport/ui/SavingsSummaryProductDetailsDrawer';
import {
    getSpendCategoryTitle,
    mergeSpendDrillInScope,
} from 'widgets/SpendSummaryReport/model/types/spendDrillIn';
import { SpendSummaryCategoriesSection } from 'widgets/SpendSummaryReport/ui/SpendSummaryCategoriesSection';
import { SpendSummaryProductsSection } from 'widgets/SpendSummaryReport/ui/SpendSummaryProductsSection';
import { SpendSummarySuppliersSection } from 'widgets/SpendSummaryReport/ui/SpendSummarySuppliersSection';
import { SpendSummaryTotalSpendMetric } from 'widgets/SpendSummaryReport/ui/SpendSummaryTotalSpendMetric';

import { useReportsPageSpendSummaryProperty } from '../model/hooks/useReportsPageSpendSummaryProperty';

import type { SpendProductRowDto } from 'entities/ReportSpendSummary';
import type { ReportWidgetInteractionPayload } from 'widgets/ComposableReportWidget';
import type { SpendDrillInLocationState } from 'widgets/SpendSummaryReport/model/types/spendDrillIn';

const ReportsPageSpendSummaryProperty = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [selectedProductSku, setSelectedProductSku] = useState<string | null>(
        null,
    );
    const {
        isSpendSummaryVisible,
        propertyId,
        propertyName,
        loading,
        summary,
        byCategory,
        bySupplier,
        products,
        totalSpend,
        spendPerUnit,
        spendPerUnitPerMonth,
        orderCount,
        spendPerOrder,
        dateFrom,
        dateTo,
        datePreset,
        lastRequestParamsPresent,
        locationState,
    } = useReportsPageSpendSummaryProperty();

    const childScope = useMemo(
        () =>
            mergeSpendDrillInScope(locationState.scope, {
                propertyIds: propertyId ? [propertyId] : undefined,
            }),
        [locationState.scope, propertyId],
    );

    const handleBack = useCallback(() => {
        navigate(getRouteReportsSpendSummary(), {
            state: { fromDrillIn: true },
        });
    }, [navigate]);

    const handleCategorySelect = useCallback(
        (payload: ReportWidgetInteractionPayload) => {
            const categoryId = payload.item.meta?.categoryId;
            if (typeof categoryId !== 'string') {
                return;
            }

            const state: SpendDrillInLocationState = {
                categoryTitle: getSpendCategoryTitle(
                    payload.item.meta?.categoryTitle,
                    payload.item.label,
                ),
                contextTotalSpend: summary?.totalSpend,
                scope: childScope,
            };
            navigate(getRouteReportsSpendSummaryCategory(categoryId), {
                state,
            });
        },
        [childScope, navigate, summary?.totalSpend],
    );

    const handleSupplierSelect = useCallback(
        ({
            supplierId,
            supplierName,
        }: {
            supplierId: string;
            supplierName?: string;
        }) => {
            const state: SpendDrillInLocationState = {
                supplierName,
                contextTotalSpend: summary?.totalSpend,
                scope: childScope,
            };
            navigate(getRouteReportsSpendSummarySupplier(supplierId), {
                state,
            });
        },
        [childScope, navigate, summary?.totalSpend],
    );

    const handleProductRowClick = useCallback(
        (sku: string, product: SpendProductRowDto) => {
            const state: SpendDrillInLocationState = {
                product,
                contextTotalSpend: summary?.totalSpend,
                scope: childScope,
            };
            navigate(getRouteReportsSpendSummaryProduct(sku), { state });
        },
        [childScope, navigate, summary?.totalSpend],
    );

    const openProductDetails = useCallback((sku: string) => {
        setSelectedProductSku(sku);
    }, []);

    const closeProductDetails = useCallback(() => {
        setSelectedProductSku(null);
    }, []);

    if (isSpendSummaryVisible === undefined) {
        return null;
    }

    if (!isSpendSummaryVisible) {
        return <Navigate to={getRouteForbidden()} replace />;
    }

    if (!propertyId || !lastRequestParamsPresent) {
        return <Navigate to={getRouteReportsSpendSummary()} replace />;
    }

    const clampedSpendPerUnit =
        spendPerUnit == null ? null : clampToZero(spendPerUnit);
    const clampedSpendPerUnitPerMonth =
        spendPerUnitPerMonth == null ? null : clampToZero(spendPerUnitPerMonth);

    return (
        <Page
            dataTestId="reports-page-spend-summary-property"
            padding="24px 35px"
        >
            <Box>
                <ReportDrillInHeader
                    title={propertyName}
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    datePreset={datePreset}
                    onBack={handleBack}
                    backButtonTitle={t('spendSummary.pdfTitle')}
                    backButtonId="back-to-spend-summary-button"
                />

                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '16px',
                        marginBottom: '24px',
                    }}
                >
                    <SpendSummaryTotalSpendMetric
                        summary={summary}
                        primaryAmount={totalSpend}
                        loading={loading}
                    />
                    <ReportMetric
                        title={t('spendSummary.spendPerUnit')}
                        primary={
                            clampedSpendPerUnit == null
                                ? '-'
                                : formatReportCurrency(clampedSpendPerUnit)
                        }
                        secondary={
                            clampedSpendPerUnitPerMonth === null
                                ? '-'
                                : `${formatReportCurrency(
                                      clampedSpendPerUnitPerMonth,
                                  )} ${t('spendSummary.perUnitPerMonthSuffix')}`
                        }
                        loading={loading}
                        minHeight="180px"
                    />
                    <ReportMetric
                        title={t('spendSummary.spendPerOrder')}
                        primary={
                            spendPerOrder == null
                                ? '-'
                                : formatReportCurrency(spendPerOrder)
                        }
                        loading={loading}
                        minHeight="180px"
                    />
                    <ReportMetric
                        title={t('Orders')}
                        primary={orderCount.toLocaleString()}
                        loading={loading}
                        minHeight="180px"
                    />
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '24px',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '24px',
                            marginBottom: '40px',
                        }}
                    >
                        <Box sx={{ flex: 1, minWidth: '360px' }}>
                            <SpendSummaryCategoriesSection
                                byCategory={byCategory}
                                loading={loading}
                                onCategorySelect={handleCategorySelect}
                                tableOnly
                                tableColumnIds={[
                                    'category',
                                    'spend',
                                    'percentOfTotal',
                                ]}
                                showBottomBorder={false}
                            />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: '360px' }}>
                            <SpendSummarySuppliersSection
                                bySupplier={bySupplier}
                                totalSpend={summary?.totalSpend}
                                loading={loading}
                                onSupplierSelect={handleSupplierSelect}
                                tableOnly
                                tableColumnIds={[
                                    'supplier',
                                    'spend',
                                    'percentOfTotal',
                                ]}
                                showBottomBorder={false}
                            />
                        </Box>
                    </Box>
                    <SpendSummaryProductsSection
                        products={products}
                        totalSpend={summary?.totalSpend}
                        loading={loading}
                        onProductDetailsClick={openProductDetails}
                        onOpenProductDrillIn={handleProductRowClick}
                        tableOnly
                        tableColumnIds={[
                            'item',
                            'brand',
                            'sku',
                            'quantity',
                            'totalSpend',
                            'percentOfTotal',
                        ]}
                        showBottomBorder={false}
                    />
                    <SavingsSummaryProductDetailsDrawer
                        sku={selectedProductSku}
                        open={Boolean(selectedProductSku)}
                        onClose={closeProductDetails}
                    />
                </Box>
            </Box>
        </Page>
    );
};

export default ReportsPageSpendSummaryProperty;
